/**
 * KWC · Hero slider (sections/kwc-hero-slider.liquid)
 * ---------------------------------------------------------------------------
 * Plain deferred script, no dependencies. Swiping is native (the slides are a
 * scroll-snap row); this adds:
 * - dots and the desktop arrows, which scroll to a slide;
 * - autoplay every data-autoplay-ms, paused while the hero is hovered, touched
 *   or has focus, while it is off screen or the tab is hidden, and never with
 *   prefers-reduced-motion;
 * - inert on the slides out of view, so keyboard focus stays on the visible one;
 * - the desktop alt text on the <img> from 750px (a <picture> has one alt).
 * A single slide (no data-multiple) gets only the alt text handling.
 */
class KwcHero extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #observer = null;
  #timer = 0;
  #index = 0;
  /** Reasons autoplay is paused right now: hover, touch, focus, offscreen, hidden. */
  #paused = new Set();
  #reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  #desktop = window.matchMedia('(min-width: 750px)');

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#syncAlt();
    this.#desktop.addEventListener('change', () => this.#syncAlt(), { signal });

    const track = this.#track;
    if (!this.hasAttribute('data-multiple') || !track) return;

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => this.#onScroll());
      },
      { passive: true, signal }
    );

    this.querySelectorAll('[data-kwc-hero-dot]').forEach((dot, i) => {
      dot.addEventListener('click', () => this.#go(i), { signal });
    });
    this.querySelector('[data-kwc-hero-prev]')?.addEventListener('click', () => this.#go(this.#index - 1), { signal });
    this.querySelector('[data-kwc-hero-next]')?.addEventListener('click', () => this.#go(this.#index + 1), { signal });

    // Pause reasons.
    this.addEventListener('pointerenter', (e) => e.pointerType === 'mouse' && this.#pause('hover'), { signal });
    this.addEventListener('pointerleave', (e) => e.pointerType === 'mouse' && this.#resume('hover'), { signal });
    let touchTimer = 0;
    this.addEventListener(
      'touchstart',
      () => {
        clearTimeout(touchTimer);
        this.#pause('touch');
      },
      { passive: true, signal }
    );
    this.addEventListener(
      'touchend',
      () => {
        clearTimeout(touchTimer);
        touchTimer = window.setTimeout(() => this.#resume('touch'), 4000);
      },
      { passive: true, signal }
    );
    this.addEventListener('focusin', () => this.#pause('focus'), { signal });
    this.addEventListener('focusout', () => !this.contains(document.activeElement) && this.#resume('focus'), { signal });
    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.#pause('hidden') : this.#resume('hidden')),
      { signal }
    );
    this.#observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.#resume('offscreen') : this.#pause('offscreen')),
      { threshold: 0.25 }
    );
    this.#observer.observe(this);
    this.#reduced.addEventListener('change', () => this.#schedule(), { signal });

    this.#schedule();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    clearTimeout(this.#timer);
  }

  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-kwc-hero-track]'));
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-kwc-hero-slide]')]);
  }

  /** @param {number} index */
  #go(index) {
    const track = this.#track;
    const count = this.#slides.length;
    if (!track || count < 2) return;
    const target = ((index % count) + count) % count;
    track.scrollTo({ left: target * track.clientWidth, behavior: this.#reduced.matches ? 'auto' : 'smooth' });
    this.#schedule();
  }

  #onScroll() {
    const track = this.#track;
    if (!track || !track.clientWidth) return;
    const index = Math.round(track.scrollLeft / track.clientWidth);
    if (index === this.#index) return;
    this.#index = index;

    this.#slides.forEach((slide, i) => {
      slide.inert = i !== index;
    });
    this.querySelectorAll('[data-kwc-hero-dot]').forEach((dot, i) => {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
    this.#schedule();
  }

  /** @param {string} reason */
  #pause(reason) {
    this.#paused.add(reason);
    clearTimeout(this.#timer);
  }

  /** @param {string} reason */
  #resume(reason) {
    this.#paused.delete(reason);
    this.#schedule();
  }

  #schedule() {
    clearTimeout(this.#timer);
    if (this.#reduced.matches || this.#paused.size > 0 || !this.hasAttribute('data-multiple')) return;
    const ms = Number(this.dataset.autoplayMs) || 6000;
    this.#timer = window.setTimeout(() => this.#go(this.#index + 1), ms);
  }

  #syncAlt() {
    for (const img of this.querySelectorAll('img[data-alt-desktop]')) {
      if (!img.dataset.altMobile) img.dataset.altMobile = img.getAttribute('alt') || '';
      img.setAttribute('alt', this.#desktop.matches ? img.dataset.altDesktop || '' : img.dataset.altMobile);
    }
  }
}

if (!customElements.get('kwc-hero')) {
  customElements.define('kwc-hero', KwcHero);
}
